import { ExternalLink, LogOut, Menu, User } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import LocationIcon from "@/assets/icons/location-icon.svg?react";
// import NotificationBingIcon from "@/assets/icons/notification-bing-icon.svg?react";
// import SearchIcon from "@/assets/icons/search-icon.svg?react";
import { useLogout } from "@/auth/hooks";
import { useAuthContext } from "@/auth/hooks/useAuthContext";
// import { Input } from "@/components/_form/Input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/Avatar";
import { Button } from "@/components/Button";
// import { IconButton } from "@/components/Button/IconButton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/DropdownMenu";
import { useSidebar } from "@/components/Sidebar";
import { ACCESS_LEVEL } from "@/constants/accessLevel";
import { LanguageSelector } from "@/widgets/LanguageSelector";

export function SiteHeader() {
  const { user } = useAuthContext();
  const { toggleSidebar } = useSidebar();
  const { t } = useTranslation("layout");
  const logout = useLogout();

  // Get user profile from localStorage (includes region, city, makhalla)
  const userProfile = useMemo(() => {
    try {
      const userString = localStorage.getItem("user");
      if (userString) {
        return JSON.parse(userString);
      }
    } catch {
      return null;
    }
    return null;
  }, [user?.id]);

  // Get user initials for avatar fallback
  const getUserInitials = (fullName?: string) => {
    if (!fullName) return "U";
    const names = fullName.split(" ");
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return fullName[0].toUpperCase();
  };

  // Get location based on access level
  const getLocationText = () => {
    const accessLevel =
      userProfile?.role?.access_level || user?.role?.access_level;

    if (!accessLevel) {
      return t("header.location");
    }

    switch (accessLevel) {
      case ACCESS_LEVEL.REPUBLIC:
        return t("header.uzbekistan");
      case ACCESS_LEVEL.REGION:
        return userProfile?.region?.name || t("header.location");
      case ACCESS_LEVEL.CITY:
        return userProfile?.city?.name || t("header.location");
      case ACCESS_LEVEL.MAKHALLA:
        return userProfile?.makhalla?.name || t("header.location");
      default:
        return t("header.uzbekistan");
    }
  };

  const userName = user?.full_name || t("header.user");
  const userRole = user?.role?.name || t("header.userRole");
  const userAvatar = user?.avatar || userProfile?.avatar || undefined;
  const userInitials = getUserInitials(user?.full_name);
  const locationText = getLocationText();

  const userAccessLevel =
    userProfile?.role?.access_level || user?.role?.access_level;
  const isMakhalla = userAccessLevel === ACCESS_LEVEL.MAKHALLA;

  return (
    <header className="flex min-h-16 w-full items-center border-b bg-background sm:min-h-20">
      <div className="flex w-full flex-wrap items-center justify-between gap-3 px-4 py-3 sm:gap-4 sm:pl-5 sm:pr-8">
        {/* Location */}
        <div className="flex min-w-0 items-center gap-2">
          <Button
            variant="ghost"
            size="link"
            className="h-8 w-8 shrink-0 rounded-md text-text-secondary hover:bg-bg-secondary hover:text-text-primary lg:hidden"
            onClick={toggleSidebar}
            aria-label="Toggle sidebar"
          >
            <Menu className="h-4 w-4" />
          </Button>
          <LocationIcon className="h-5 w-5" />
          <span className="truncate text-xs font-medium text-text-secondary sm:text-sm">
            {locationText}
          </span>
        </div>

        {/* Search Input */}
        {/* <div className="w-full max-w-[500px]">
          <Input
            type="search"
            iconLeft={
              <SearchIcon className="h-4 min-w-4 text-text-secondary" />
            }
            placeholder={t("header.searchPlaceholder")}
            className="h-[38px] w-full border-border-secondary p-2.5 text-text-secondary transition-all duration-300 focus-within:ring-offset-[0.3px] hover:border-border-brand"
            inputClassName="text-text-primary"
          />
        </div> */}

        {/* Right Group: Language Selector, Notification Button, User Profile */}
        <div className="ml-auto flex shrink-0 items-center gap-3 sm:gap-6">
          {/* Language Selector */}
          <LanguageSelector />

          {/* Notification Button */}
          {/* <IconButton variant="ghost" size="default" className="relative">
            <NotificationBingIcon className="h-5 min-w-5" />
          </IconButton> */}

          {/* User Profile with Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div className="flex cursor-pointer items-center gap-3 transition-opacity hover:opacity-80">
                <div className="hidden min-w-0 flex-col items-end sm:flex">
                  <p className="text-sm font-medium text-text-primary">
                    {userName}
                  </p>
                  <p className="text-xs font-normal text-text-secondary">
                    {userRole}
                  </p>
                </div>
                <Avatar className="h-9 w-9">
                  <AvatarImage src={userAvatar} alt="User" />
                  <AvatarFallback className="bg-bg-brand-solid p-1 text-text-white">
                    {userInitials}
                  </AvatarFallback>
                </Avatar>
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end">
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">{userName}</p>
                  <p className="text-muted-foreground text-xs leading-none">
                    {userRole}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {isMakhalla && (
                <DropdownMenuItem asChild className="hover:bg-slate-100">
                  <a
                    href="https://yoshlartizimi.uz/landing"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="mr-2 h-4 w-4" />
                    <span>KPI</span>
                  </a>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem asChild className="hover:bg-slate-100">
                <Link to="/profile">
                  <User className="mr-2 h-4 w-4" />
                  <span>{t("header.profile")}</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={logout} className="hover:bg-slate-100">
                <LogOut className="mr-2 h-4 w-4" />
                <span>{t("header.logout")}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
